import { DateTime } from 'luxon';
import { PaymentForm } from '../../interfaces/Checkout/PaymentForm';
import {
  validateCardNumber,
  validateCvc,
  validateEmail,
  validatePhoneNumber,
} from '../../utils/Validation';
import { isTokenValid } from '../../utils/Auth';
import { useLocalizedText } from '../../hooks/useLocalizedText';

export type CheckoutStep = 'profile' | 'address' | 'payment';
export const checkoutSteps: CheckoutStep[] = ['profile', 'address', 'payment'];

export const useValidateProfile = (form: PaymentForm) => {
  const t = useLocalizedText();

  const errors: Record<string, string> = {};
  if (!form.firstName.length) {
    errors.firstName = t('validation.field_is_required');
  }
  if (!form.lastName.length) {
    errors.lastName = t('validation.field_is_required');
  }
  if (!validateEmail(form.email)) {
    errors.email = t('validation.valid_email');
  }
  if (!validatePhoneNumber(form.phone)) {
    errors.phone = t('validation.phone_number_length');
  }

  return errors;
};

export const useValidateAddress = (form: PaymentForm) => {
  const t = useLocalizedText();

  const errors: Record<string, string> = {};

  if (isTokenValid()) {
    if (!form.addressID) {
      errors.addressID = t('checkout.validation.select_shipping_address');
    }
    if (!form.billingAddressID) {
      errors.billingAddressID = t('checkout.validation.select_billing_address');
    }

    return errors;
  }

  if (!form.guestAddress?.address.length) {
    errors.address = t('validation.field_is_required');
  }
  if ((form.guestAddress?.address.length ?? 0) < 10) {
    errors.address = t('checkout.validation.address_length');
  }
  if (form.guestAddress?.addressType === 1) {
    if (!form.guestAddress.taxNumber?.length) {
      errors.taxNumber = t('validation.field_is_required');
    }
    if (!form.guestAddress.taxOffice?.length) {
      errors.taxOffice = t('validation.field_is_required');
    }
  }

  return errors;
};

export const useValidatePayment = (form: PaymentForm) => {
  const t = useLocalizedText();

  if (form.paymentMethod === 'bank_transfer') {
    return {};
  }

  const errors: Record<string, string> = {};

  if (!form.creditCardForm.cardOwner.length) {
    errors.cardOwner = t('validation.field_is_required');
  }
  if (!validateCardNumber(form.creditCardForm.cardNumber)) {
    errors.cardNumber = t('checkout.validation.card_number');
  }

  const now = DateTime.now();
  const currentMonth = now.month;
  const currentYear = now.year;
  if (
    form.creditCardForm.expiryYear < currentYear ||
    (form.creditCardForm.expiryYear === currentYear &&
      form.creditCardForm.expiryMonth < currentMonth)
  ) {
    errors.expiryYear = t('checkout.validation.valid_expiration_date');
  }

  if (!validateCvc(form.creditCardForm.cvc)) {
    errors.cvc = t('checkout.validation.invalid_cvv');
  }

  return errors;
};
